import { SubmissionError } from "redux-form";
import { difference, identity } from "ramda";
import moment from "moment";
import api from "api/flexapi";
import { pushDataLayer } from "analytics";
import * as ClaimAction from "./claimActionTypes";
import { captureSystemError } from "./systemMessageActions";
import { resolveResponseError, captureErrorToDispatch } from "api/promise-helpers";
import { showToast } from "./toastActions";
import messages from "../components/messagesDescription";

export const loadClaimLimitMessages = (
  claimantId,
  claimantType,
  claimItemId,
  receiptDate,
  selectedDelegator
) => dispatch => {
  const meta = { claimantId, claimantType, claimItemId, receiptDate, selectedDelegator };

  removeUndefinedObjectValue(meta);

  dispatch({ type: ClaimAction.FETCH_CLAIM_LIMIT_MESSAGES_REQUEST, meta });
  api
    .getClaimLimitMessages(meta)
    .then(response =>
      dispatch({
        type: ClaimAction.FETCH_CLAIM_LIMIT_MESSAGES_SUCCESS,
        meta,
        payload: response.body,
      })
    )
    .catch(err =>
      dispatch({
        type: ClaimAction.FETCH_CLAIM_LIMIT_MESSAGES_FAILURE,
        meta,
        payload: err.response ? err.response.body : err,
      })
    );
};

export const loadClaimItemAccounts = (planId, claimTypeId, selectedDelegator) => dispatch => {
  const meta = { planId, claimTypeId };
  dispatch({ type: ClaimAction.FETCH_CLAIM_ITEM_ACCOUNTS_BEGIN, meta });

  const data = { planId, claimTypeId, selectedDelegator };

  removeUndefinedObjectValue(data);

  api
    .getClaimItemAccounts(data)
    .then(({ body = { accounts: [] } }) => ({
      ...body,
      accounts: body.accounts.sort((a, b) => (a.sequence > b.sequence ? 1 : -1)),
    }))
    .then(data =>
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEM_ACCOUNTS_SUCCESS,
        meta,
        payload: { ...data },
      })
    )
    .catch(err =>
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEM_ACCOUNTS_ERROR,
        meta,
        payload: err.response ? err.response.body : err,
      })
    );
};

export const loadAccounts = (
  planYear,
  effectiveDate,
  includingHidden = false,
  selectedDelegator
) => dispatch => {
  const meta = { planYear, effectiveDate, includingHidden };
  const data = {
    ...(effectiveDate !== null ? { effectiveDate } : {}),
    ...(includingHidden !== null ? { includingHidden } : {}),
    selectedDelegator,
  };

  // remove object with value undefined or null
  removeUndefinedObjectValue(data);

  dispatch({ type: ClaimAction.FETCH_ACCOUNTS_BEGIN, meta });
  api
    .getSpendingAccounts(data)
    .then(response =>
      dispatch({
        type: ClaimAction.FETCH_ACCOUNTS_SUCCESS,
        meta,
        payload: response.body,
      })
    )
    .catch(err =>
      dispatch({
        type: ClaimAction.FETCH_ACCOUNTS_ERROR,
        meta,
        payload: err.response ? err.response.body : err,
      })
    );
};

export const loadPendingClaims = (
  benefitPeriod,
  selectedDelegator,
  getDonateCategory = false
) => dispatch => {
  dispatch({ type: ClaimAction.FETCH_PENDING_CLAIMS_BEGIN, payload: null });

  const data = { benefitPeriod, selectedDelegator, getDonateCategory };
  removeUndefinedObjectValue(data);

  api
    .getPendingClaims(data)
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_PENDING_CLAIMS_SUCCESS,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_PENDING_CLAIMS_ERROR,
        payload: err.response ? err.response.body : err,
      });
    });
};

export const loadViewClaim = (
  claimId,
  selectedDelegator,
  getDonateCategory = false
) => dispatch => {
  dispatch({ type: ClaimAction.FETCH_CLAIM_BEGIN, claimId, payload: null });

  const data = { claimId, selectedDelegator, getDonateCategory };

  removeUndefinedObjectValue(data);

  api
    .getClaim(data)
    .then(response => {
      const { benefitPeriod, claimTypeId } = response.body;
      const meta = { planId: benefitPeriod, claimItemId: claimTypeId, claimId };

      dispatch({
        type: ClaimAction.FETCH_CLAIM_SUCCESS,
        claimId,
        payload: response.body,
      });
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEM_FIELDS_SUCCESS,
        meta,
        payload: response.body,
      });
    })
    .catch(e =>
      Promise.resolve(e)
        .then(resolveResponseError)
        .then(
          captureErrorToDispatch(dispatch, captureSystemError, {
            msgLangId: "resourceDoesNotExist",
            resource: `/claims/view/${claimId}`,
            module: "claims",
          })
        )
    );
};

export const loadClaim = (claimId, selectedDelegator) => dispatch => {
  dispatch({ type: ClaimAction.FETCH_CLAIM_BEGIN, claimId, payload: null });

  const data = { claimId, selectedDelegator };

  removeUndefinedObjectValue(data);

  api
    .getClaim(data)
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_SUCCESS,
        claimId,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ERROR,
        claimId,
        payload: err.response ? err.response.body : err,
      });
    });
};

const fetchClaimItemFields = (
  planId,
  claimItemId,
  claimId,
  page,
  selectedDelegator,
  getDonateCategory
) => dispatch => {
  const meta = { planId, claimItemId, claimId, page };

  dispatch({ type: ClaimAction.FETCH_CLAIM_ITEM_FIELDS_BEGIN, meta });

  const data = { planId, claimItemId, objectId: claimId, selectedDelegator, getDonateCategory };

  removeUndefinedObjectValue(data);

  api
    .getClaimItemFields(data)
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEM_FIELDS_SUCCESS,
        meta,
        payload: response.body,
      });
      return response.body;
    })
    .then(fieldsData => {
      const { fields, missingBankMessage } = fieldsData;
      for (const field of fields) {
        if (
          (field.fieldId === "BankCode" && !field.value) ||
          (field.fieldId === "BranchCode" && !field.value) ||
          (field.fieldId === "BankAccountNo" && !field.value)
        ) {
          dispatch(
            showToast({
              message: missingBankMessage || "Missing bank details. Please contact HR to update.",
            })
          );
          break;
        }
      }
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEM_FIELDS_ERROR,
        meta,
        payload: err.response ? err.response.body : err,
      });
    });
};

const fetchClaims = (claimListType, selectedDelegator, getDonateCategory) => {
  const meta = { claimListType };
  // Return thunk middleware
  return dispatch => {
    dispatch({
      type: ClaimAction.FETCH_CLAIMS_BEGIN,
      meta,
      payload: null,
    });

    const data = { benefitPeriod: claimListType, selectedDelegator, getDonateCategory };

    removeUndefinedObjectValue(data);

    api
      .getClaimsHistory(data)
      .then(response => {
        dispatch({
          type: ClaimAction.FETCH_CLAIMS_SUCCESS,
          meta,
          payload: response.body,
        });
      })
      .catch(err => {
        dispatch({
          type: ClaimAction.FETCH_CLAIMS_ERROR,
          meta,
          payload: err.response ? err.response.body : err,
        });
      });
  };
};

export const loadClaimsHistory = (benefitPeriod, selectedDelegator, getDonateCategory = false) =>
  fetchClaims(benefitPeriod, selectedDelegator, getDonateCategory);

export const loadClaimItemFields = (
  planId,
  claimItemId,
  claimId,
  page,
  selectedDelegator,
  getDonateCategory
) => fetchClaimItemFields(planId, claimItemId, claimId, page, selectedDelegator, getDonateCategory);

const getAnalyticsLabelFromClaimData = data => {
  const { benefitPeriod, claimTypeId, claimantName } = data;

  return [
    window.location.href,
    moment().format(moment.defaultFormatUtc),
    benefitPeriod,
    claimTypeId,
    claimantName,
  ].join("@");
};

/* APL-4766 Retry Failed Claim Upload */
const getEnhancePostReceiptAgentWithRetry = (data, file) => agent =>
  agent.retry(process.env.CLAIM_UPLOAD_RETRY_LIMIT, function shouldRetry(err, res) {
    if (res.statusCode === 301 || res.statusCode === 405) {
      const label = [
        getAnalyticsLabelFromClaimData(data),
        `Retry:${this._retries}`,
        file.name,
        file.size,
        file.type,
      ].join("@");

      pushDataLayer({
        event: "Claim Submission Errors",
        event_action: "Upload Claim Receipt Error",
        event_label: label,
      });

      return true;
    }
    return false;
  });

export const submitClaim = (data, dispatch, props) => {
  dispatch({ type: ClaimAction.SUBMIT_CLAIM_UPLOAD_BEGIN, payload: data });
  const { claimItem, claim } = props;
  const { receipts, referral, claimTypeId, userAccepted, limitAccepted, ...restData } = data;
  const { selectedDelegator } = restData;
  const claimItemName = claimItem.claimName;
  const originalReceiptIds =
    claim && claim.receipts && claim.receipts.map(item => item.pkDigitalContent);

  removeUndefinedObjectValue(restData);

  // Separate IDs and New files
  const { receiptIds, receiptFiles } = receipts
    ? receipts.reduce(
        (obj, item) =>
          item.pkDigitalContent
            ? { ...obj, receiptIds: [...obj.receiptIds, item.pkDigitalContent] }
            : { ...obj, receiptFiles: [...obj.receiptFiles, item] },
        { receiptIds: [], receiptFiles: [] }
      )
    : { receiptIds: null, receiptFiles: null };

  const { referralId, referralFile } =
    referral && referral.pkDigitalContent
      ? { referralId: referral.pkDigitalContent, referralFile: null }
      : { referralId: null, referralFile: referral };

  const receiptsPromises =
    receiptFiles &&
    Promise.all(
      receiptFiles.map(item => {
        const enhancePostReceiptAgent = process.env.CLAIM_UPLOAD_RETRY_LIMIT
          ? getEnhancePostReceiptAgentWithRetry(data, item)
          : identity;
        return api.postClaimReceipt(item, selectedDelegator, enhancePostReceiptAgent);
      })
    );

  const referralPromise = referralFile && api.postClaimReferral(referralFile, selectedDelegator);

  return Promise.all([receiptsPromises, referralPromise])
    .then(responses => {
      dispatch({
        type: ClaimAction.SUBMIT_CLAIM_UPLOAD_SUCCESS,
        meta: data,
        payload: responses,
      });

      const [receiptsResponses, referralResponse] = responses;
      const referralPkDigitalContent =
        referralResponse && referralResponse.body ? referralResponse.body.pkDigitalContent : null;
      const finalReferralId = referralId ? referralId : referralPkDigitalContent;
      const finalReceiptIds = []
        .concat(
          receiptIds,
          receiptsResponses
            ? receiptsResponses.map(response => response.body.pkDigitalContent)
            : null
        )
        .filter(item => item);

      const additionalProps = {};
      if (finalReferralId) {
        additionalProps.referralFileId = finalReferralId;
      }
      if (finalReceiptIds && finalReceiptIds.length > 0) {
        additionalProps.receiptFilesIds = finalReceiptIds;
      }

      const payload = {
        ...restData,
        claimTypeId,
        claimItemName,
        ...additionalProps,
      };

      dispatch({ type: ClaimAction.SUBMIT_CLAIM_BEGIN, payload });

      return api.postClaim(payload, userAccepted,limitAccepted).then(res => {
        const deletedReceiptIds =
          originalReceiptIds && receiptIds && difference(originalReceiptIds, receiptIds);
        deletedReceiptIds &&
          Promise.all(
            deletedReceiptIds.map(id =>
              api.deleteClaimReceipt(restData.objectId, id, selectedDelegator)
            )
          );
        return res;
      });
    })
    .catch(err => {
      if (err.status === 409 || err.status === 430) {
        throw new SubmissionError({
          _error: {
            message: err.response ? err.response.body : err,
            code: err.status,
            reason: "ASK_USER_CONFIRMATION",
          },
        });
      } else {
        if (process.env.CLAIM_UPLOAD_RETRY_LIMIT && err.status === 405) {
          pushDataLayer({
            event: "Claim Submission Errors",
            event_action: "Failed Retrying Claim Receipt Upload",
            event_label: getAnalyticsLabelFromClaimData(data),
          });
        }
        throw new SubmissionError({
          _error: err.response ? err.response.body : err,
        });
      }
    });
};

export const loadShouldDisplayClaimItemsFields = () => dispatch => {
  dispatch({
    type: ClaimAction.DISPLAY_CLAIM_ITEM_FIELDS_REQUEST,
  });

  return api
    .getDisplayClaimItemFields()
    .then(response => {
      dispatch({
        type: ClaimAction.DISPLAY_CLAIM_ITEM_FIELDS_SUCCESS,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.DISPLAY_CLAIM_ITEM_FIELDS_FAILURE,
        payload: err.response ? err.response.body : err,
      });
    });
};

export const submitClaimSuccess = (response, dispatch, props) => {
  const { mode, showToast, push, selectedDelegator, getDonateCategory, intl } = props;
  dispatch({ type: ClaimAction.SUBMIT_CLAIM_SUCCESS, payload: response.body });
  const { claimObjectId, id } = response.body;

  const actionType = getDonateCategory
    ? intl.formatMessage(messages.claims.donationDescription)
    : intl.formatMessage(messages.claims.claimDescription);

  const status =
    mode === "edit"
      ? intl.formatMessage(messages.claims.savedDescription)
      : intl.formatMessage(messages.claims.submittedDescription);

  const success = intl.formatMessage(messages.claims.successfullyDescription);

  showToast({
    message: `${actionType} #${id} ${status} ${success}.`,
  });

  if (mode === "edit") {
    push(
      `/${getDonateCategory ? "donations" : "claims"}/view/${claimObjectId}/${selectedDelegator ||
        ""}`
    );
  } else {
    const delegator = `/${selectedDelegator}` || "";

    push(
      `/${getDonateCategory ? "donations" : "claims"}/view/${claimObjectId}${delegator}?status=new`
    );
  }
};

export const deleteClaim = (claimId, selectedDelegator) => dispatch => {
  dispatch({ type: ClaimAction.DELETE_CLAIM_BEGIN, claimId, payload: null });

  const data = { claimId, selectedDelegator };

  removeUndefinedObjectValue(data);

  return api
    .deleteClaim(data)
    .then(response => {
      dispatch({
        type: ClaimAction.DELETE_CLAIM_SUCCESS,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.DELETE_CLAIM_ERROR,
        payload: err.response ? err.response.body : err,
      });
    });
};

const removeUndefinedObjectValue = data => {
  Object.keys(data).forEach(
    key =>
      (data[key] === undefined ||
        data[key] === null ||
        (key === "selectedDelegator" && data[key] === "Myself")) &&
      delete data[key]
  );

  return data;
};

const fetchClaimItems = (
  planId,
  isServiceProviderClaimItem = false,
  selectedDelegator,
  getDonateCategory = false
) => dispatch => {
  const meta = { planId };
  dispatch({ type: ClaimAction.FETCH_CLAIM_ITEMS_BEGIN, meta, payload: null });

  const data = { planId, isServiceProviderClaimItem, selectedDelegator, getDonateCategory };

  // remove object with value undefined or null
  removeUndefinedObjectValue(data);

  api
    .getClaimItems(data)
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEMS_SUCCESS,
        meta,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_CLAIM_ITEMS_ERROR,
        meta,
        payload: err.response ? err.response.body : err,
      });
    });
};

export const loadClaimItems = (
  planId,
  isServiceProviderClaimItem = false,
  selectedDelegator,
  getDonateCategory = false
) => fetchClaimItems(planId, isServiceProviderClaimItem, selectedDelegator, getDonateCategory);

const fetchBenefitPlans = () => dispatch => {
  dispatch({ type: ClaimAction.FETCH_BENEFIT_PLANS_BEGIN, payload: null });
  api
    .getBenefitPlans()
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_BENEFIT_PLANS_SUCCESS,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_BENEFIT_PLANS_ERROR,
        payload: err.response ? err.response.body : err,
      });
    });
};

export const loadBenefitPlans = () => fetchBenefitPlans();

// eslint-disable-next-line security/detect-non-literal-fs-filename
export const fetchPendingClaimsReport = url => () => window.open(url);

// eslint-disable-next-line security/detect-non-literal-fs-filename
export const fetchHistoricalClaimsReport = url => () => window.open(url);

export const changeReferralFileName = (change, data) => dispatch => {
  // Form name, fieldid, value
  dispatch(change(data.form, data.fieldId, data.value));
};

export const getEmployeesByDelegation = () => dispatch => {
  dispatch({ type: ClaimAction.FETCH_EMPLOYEES_BY_DELEGATION_BEGIN });

  return api
    .getEmployeesByDelegation()
    .then(({ body }) => {
      const meta = {
        delegatedEmployeeName: "Myself",
      };

      dispatch({
        type: ClaimAction.FETCH_EMPLOYEES_BY_DELEGATION_SUCCESS,
        payload: body.length > 0 ? [].concat(meta).concat(body) : body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_EMPLOYEES_BY_DELEGATION_ERROR,
        payload: err.response ? err.response.body : err,
      });
    });
};

export const changeCurrencyCode = ({ receiptCurrency = null }) => {
  return dispatch => {
    dispatch({
      type: ClaimAction.CHANGE_CURRENCY_CODE,
      payload: { receiptCurrency },
    });
  };
};

export const changeCurrencyRate = (convertedAmount, toCurrencyCode, amount) => {
  return dispatch => {
    dispatch({
      type: ClaimAction.CHANGE_CURRENCY_RATE,
      payload: {
        receiptAmount: convertedAmount,
        foreignReceiptAmount: amount,
        toCurrencyCode,
      },
    });
  };
};

export const getBalanceDetails = (planYear, accountType) => dispatch => {
  dispatch({ type: ClaimAction.FETCH_BALANCE_DETAIL_BEGIN, payload: null });
  api
    .getBalanceDetails(planYear, accountType)
    .then(response => {
      dispatch({
        type: ClaimAction.FETCH_BALANCE_DETAIL_SUCCESS,
        payload: response.body,
      });
    })
    .catch(err => {
      dispatch({
        type: ClaimAction.FETCH_BALANCE_DETAIL_ERROR,
        payload: err.response ? err.response.body : err,
      });
    });
};



// WEBPACK FOOTER //
// ./actions/claimActions.js